/**
 * Pipedrive Integration Factory
 * Creates and configures Pipedrive CRM integration instances
 */

import { PipedriveIntegration, PipedriveConfig } from './PipedriveIntegration';
import { IntegrationConfig, ValidationResult } from '../../../types/integration';
import { BaseIntegration } from '../base/BaseIntegration';
import { IntegrationFactory } from '../registry/IntegrationRegistry';

export class PipedriveIntegrationFactory implements IntegrationFactory {
  /**
   * Create a new Pipedrive integration instance (IntegrationFactory interface)
   */
  async create(config: IntegrationConfig): Promise<BaseIntegration> {
    return PipedriveIntegrationFactory.create(config) as unknown as BaseIntegration;
  }

  /**
   * Validate configuration (IntegrationFactory interface)
   */
  async validate(config: Partial<IntegrationConfig>): Promise<ValidationResult> {
    // Convert to PipedriveConfig for validation
    const pipedriveConfig = config as Partial<PipedriveConfig>;
    const validation = PipedriveIntegrationFactory.validateConfig(pipedriveConfig as PipedriveConfig);
    
    return {
      valid: validation.valid,
      errors: validation.errors.map(error => ({
        field: 'config',
        message: error,
        code: 'VALIDATION_ERROR'
      })),
      warnings: []
    };
  }

  /**
   * Get configuration schema (IntegrationFactory interface)
   */
  getConfigSchema(): Record<string, any> {
    return {
      type: 'object',
      required: ['apiToken', 'companyDomain'],
      properties: {
        apiToken: {
          type: 'string',
          title: 'API Token',
          description: 'Your Pipedrive API token',
          pattern: '^[a-f0-9]{40}$',
          minLength: 40,
          maxLength: 40
        },
        companyDomain: {
          type: 'string',
          title: 'Company Domain',
          description: 'Your Pipedrive company domain (e.g., "mycompany" for mycompany.pipedrive.com)',
          pattern: '^[a-zA-Z0-9-]+$',
          minLength: 1,
          maxLength: 50
        }
      },
      additionalProperties: false
    };
  }

  /**
   * Get supported features (IntegrationFactory interface)
   */
  getSupportedFeatures(): string[] {
    return [
      'bidirectional_sync',
      'real_time_sync',
      'webhook_support',
      'batch_operations',
      'custom_fields',
      'api_key_authentication',
      'contact_sync',
      'deal_sync',
      'organization_sync',
      'activity_logging',
      'conflict_resolution',
      'field_mapping',
      'rate_limiting'
    ];
  }

  /**
   * Create a new Pipedrive integration instance (static method)
   */
  static create(config: IntegrationConfig | PipedriveConfig): PipedriveIntegration {
    // Convert IntegrationConfig to PipedriveConfig if needed
    let pipedriveConfig: PipedriveConfig;
    
    if ('apiToken' in config) {
      // Already a PipedriveConfig
      pipedriveConfig = config as PipedriveConfig;
    } else {
      // Convert from IntegrationConfig
      const baseConfig = config as IntegrationConfig;
      pipedriveConfig = {
        id: baseConfig.id,
        name: baseConfig.name,
        type: baseConfig.type,
        platform: baseConfig.platform || 'pipedrive',
        status: baseConfig.status,
        apiToken: '', // Will be set from credentials
        companyDomain: '', // Will be set from credentials
        credentials: baseConfig.credentials,
        settings: baseConfig.settings,
        fieldMappings: baseConfig.fieldMappings,
        syncSchedule: baseConfig.syncSchedule,
        webhookConfig: baseConfig.webhookConfig,
        createdAt: baseConfig.createdAt,
        updatedAt: baseConfig.updatedAt,
        lastSyncAt: baseConfig.lastSyncAt,
        healthStatus: baseConfig.healthStatus,
        metadata: baseConfig.metadata
      };
    }
    
    // Validate required Pipedrive-specific configuration
    if (!pipedriveConfig.apiToken) {
      throw new Error('Pipedrive API token is required');
    }
    
    if (!pipedriveConfig.companyDomain) {
      throw new Error('Pipedrive company domain is required');
    }

    // Set default configuration values
    const defaultConfig: Partial<PipedriveConfig> = {
      settings: {
        enabled: true,
        autoSync: true,
        batchSize: 100,
        rateLimitPerMinute: 600,
        retryAttempts: 3,
        timeoutMs: 30000,
        customSettings: {
          syncFrequency: 'hourly',
          retryDelay: 1000,
          webhookRetryAttempts: 5
        },
        ...pipedriveConfig.settings
      },
      fieldMappings: pipedriveConfig.fieldMappings.length > 0 ? pipedriveConfig.fieldMappings : [
        {
          id: 'email_mapping',
          sourceField: 'email',
          targetField: 'email[0].value',
          direction: 'bidirectional',
          required: true,
          dataType: 'string'
        },
        {
          id: 'firstName_mapping',
          sourceField: 'firstName',
          targetField: 'first_name',
          direction: 'bidirectional',
          required: false,
          dataType: 'string'
        },
        {
          id: 'lastName_mapping',
          sourceField: 'lastName',
          targetField: 'last_name',
          direction: 'bidirectional',
          required: false,
          dataType: 'string'
        },
        {
          id: 'phone_mapping',
          sourceField: 'phone',
          targetField: 'phone[0].value',
          direction: 'bidirectional',
          required: false,
          dataType: 'string'
        },
        {
          id: 'company_mapping',
          sourceField: 'company',
          targetField: 'org_id',
          direction: 'bidirectional',
          required: false,
          dataType: 'string'
        }
      ]
    };

    // Merge default config with provided config
    const finalConfig: PipedriveConfig = {
      ...defaultConfig,
      ...pipedriveConfig,
      settings: {
        ...defaultConfig.settings,
        ...pipedriveConfig.settings
      }
    };

    return new PipedriveIntegration(finalConfig);
  }

  /**
   * Create a Pipedrive integration with minimal configuration
   */
  static createWithCredentials(
    apiToken: string,
    companyDomain: string,
    options: Partial<PipedriveConfig> = {}
  ): PipedriveIntegration {
    const config: PipedriveConfig = {
      id: options.id || `pipedrive_${Date.now()}`,
      name: options.name || 'Pipedrive Integration',
      type: 'crm',
      platform: 'pipedrive',
      status: 'inactive',
      apiToken,
      companyDomain,
      credentials: {
        encrypted: JSON.stringify({
          apiToken,
          companyDomain
        }),
        keyId: 'default',
        algorithm: 'AES-256-GCM',
        createdAt: new Date()
      },
      settings: {
        enabled: true,
        autoSync: true,
        batchSize: 100,
        rateLimitPerMinute: 600,
        retryAttempts: 3,
        timeoutMs: 30000,
        customSettings: {
          syncFrequency: 'hourly',
          retryDelay: 1000,
          webhookRetryAttempts: 5,
          ...options.settings?.customSettings
        }
      },
      fieldMappings: options.fieldMappings || [],
      syncSchedule: options.syncSchedule || {
        enabled: true,
        frequency: 'hourly',
        timezone: 'UTC',
        batchSize: 100,
        conflictResolution: 'manual'
      },
      createdAt: new Date(),
      updatedAt: new Date(),
      healthStatus: {
        status: 'unhealthy',
        lastCheck: new Date(),
        responseTime: 0,
        errorRate: 0,
        uptime: 0,
        issues: []
      },
      metadata: {
        description: 'Pipedrive CRM integration',
        tags: ['pipedrive', 'crm'],
        category: 'CRM',
        version: '1.0.0',
        supportedFeatures: ['contacts', 'deals', 'api_key'],
        limitations: [],
        customData: {}
      },
      ...options
    };

    return this.create(config);
  }

  /**
   * Validate Pipedrive configuration
   */
  static validateConfig(config: PipedriveConfig): { valid: boolean; errors: string[] } {
    const errors: string[] = [];

    // Required fields validation
    if (!config.apiToken || config.apiToken.trim() === '') {
      errors.push('API token is required');
    }

    if (!config.companyDomain || config.companyDomain.trim() === '') {
      errors.push('Company domain is required');
    }

    // API token format validation (basic check)
    if (config.apiToken && !config.apiToken.match(/^[a-f0-9]{40}$/)) {
      errors.push('API token appears to be invalid format (should be 40 character hex string)');
    }

    // Company domain format validation
    if (config.companyDomain && !config.companyDomain.match(/^[a-zA-Z0-9-]+$/)) {
      errors.push('Company domain should only contain letters, numbers, and hyphens');
    }

    // Field mappings validation
    if (config.fieldMappings) {
      config.fieldMappings.forEach((mapping, index) => {
        if (!mapping.sourceField || !mapping.targetField) {
          errors.push(`Field mapping ${index + 1} is missing source or target field`);
        }
        
        if (!['bidirectional', 'to_platform', 'from_platform'].includes(mapping.direction)) {
          errors.push(`Field mapping ${index + 1} has invalid direction`);
        }
      });
    }

    // Settings validation
    if (config.settings) {
      if (config.settings.batchSize && (config.settings.batchSize < 1 || config.settings.batchSize > 500)) {
        errors.push('Batch size must be between 1 and 500');
      }

      if (config.settings.retryAttempts && (config.settings.retryAttempts < 0 || config.settings.retryAttempts > 10)) {
        errors.push('Retry attempts must be between 0 and 10');
      }

      if (config.settings.customSettings?.retryDelay && config.settings.customSettings.retryDelay < 100) {
        errors.push('Retry delay must be at least 100ms');
      }
    }

    return {
      valid: errors.length === 0,
      errors
    };
  }

  /**
   * Get default field mappings for Pipedrive
   */
  static getDefaultFieldMappings() {
    return [
      {
        sourceField: 'email',
        targetField: 'email[0].value',
        direction: 'bidirectional' as const,
        required: true,
        dataType: 'string' as const,
        description: 'Primary email address'
      },
      {
        sourceField: 'firstName',
        targetField: 'first_name',
        direction: 'bidirectional' as const,
        required: false,
        dataType: 'string' as const,
        description: 'First name'
      },
      {
        sourceField: 'lastName',
        targetField: 'last_name',
        direction: 'bidirectional' as const,
        required: false,
        dataType: 'string' as const,
        description: 'Last name'
      },
      {
        sourceField: 'phone',
        targetField: 'phone[0].value',
        direction: 'bidirectional' as const,
        required: false,
        dataType: 'string' as const,
        description: 'Primary phone number'
      },
      {
        sourceField: 'company',
        targetField: 'org_id',
        direction: 'bidirectional' as const,
        required: false,
        dataType: 'string' as const,
        description: 'Organization/Company'
      }
    ];
  }

  /**
   * Create test configuration for development/testing
   */
  static createTestConfig(): PipedriveConfig {
    return {
      id: 'pipedrive_test',
      name: 'Pipedrive Test Integration',
      type: 'crm',
      platform: 'pipedrive',
      status: 'inactive',
      apiToken: 'test_api_token_1234567890abcdef1234567890abcdef12345678',
      companyDomain: 'test-company',
      credentials: {
        encrypted: JSON.stringify({
          apiToken: 'test_api_token_1234567890abcdef1234567890abcdef12345678',
          companyDomain: 'test-company'
        }),
        keyId: 'test_key',
        algorithm: 'AES-256-GCM',
        createdAt: new Date()
      },
      settings: {
        enabled: false,
        autoSync: false,
        batchSize: 10,
        rateLimitPerMinute: 60,
        retryAttempts: 1,
        timeoutMs: 5000,
        customSettings: {
          syncFrequency: 'manual',
          retryDelay: 500,
          webhookRetryAttempts: 2
        }
      },
      fieldMappings: this.getDefaultFieldMappings().map(mapping => ({
        ...mapping,
        id: `test_${mapping.sourceField}_mapping`
      })),
      syncSchedule: {
        enabled: false,
        frequency: 'manual',
        timezone: 'UTC',
        batchSize: 10,
        conflictResolution: 'manual'
      },
      createdAt: new Date(),
      updatedAt: new Date(),
      healthStatus: {
        status: 'unhealthy',
        lastCheck: new Date(),
        responseTime: 0,
        errorRate: 0,
        uptime: 0,
        issues: []
      },
      metadata: {
        description: 'Test Pipedrive integration for development',
        tags: ['test', 'pipedrive', 'crm'],
        category: 'CRM',
        version: '1.0.0',
        supportedFeatures: ['contacts', 'deals', 'api_key'],
        limitations: ['test environment only'],
        customData: {}
      }
    };
  }
}

// Export singleton instance
export const pipedriveIntegrationFactory = new PipedriveIntegrationFactory();